import type { Agent } from "@/types/aiToolkit";
import type { AgentHandoffSource } from "@/types/agentHandoff";
import type { AgentActionType } from "@/lib/agentActionRouter";
import type { ClickUpActionContext } from "@/hooks/useCommunicationAnalyze";
import type { AnalyzeItemInput } from "@/hooks/useCommunicationAnalyze";
import type { AgentAnalysisResult } from "@/types/communications";
import { agentAllowsChatHandoff, getChatHandoffPermissions } from "@/lib/agentChatHandoff";
import { createHandoffSession } from "@/lib/agentHandoffSession";
import { buildHandoffChatSeed } from "@/lib/agentHandoffMerge";

export interface StartAgentHandoffParams {
  agent: Agent;
  source: AgentHandoffSource;
  returnPath: string;
  items: AnalyzeItemInput[];
  results: AgentAnalysisResult[];
  actionType: AgentActionType;
  clickUpContext?: ClickUpActionContext;
  onIndexForRag?: () => Promise<void>;
}

export async function startAgentHandoffChat(
  params: StartAgentHandoffParams
): Promise<string | null> {
  if (!agentAllowsChatHandoff(params.agent)) return null;

  const perms = getChatHandoffPermissions(params.agent);
  if (perms?.autoIndexForRag && params.onIndexForRag) {
    try {
      await params.onIndexForRag();
    } catch {
      /* non-blocking */
    }
  }

  const session = createHandoffSession({
    agentId: params.agent.id,
    agentName: params.agent.name,
    source: params.source,
    returnPath: params.returnPath,
    items: params.items,
    results: params.results,
    actionType: params.actionType,
    clickUpContext: params.clickUpContext
      ? {
          ...params.clickUpContext,
          meetings: params.clickUpContext.meetings,
        }
      : undefined,
  });

  const seed = buildHandoffChatSeed({
    agentName: params.agent.name,
    source: params.source,
    items: params.items,
    results: params.results,
    clickUpContext: params.clickUpContext,
  });

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(`agent-handoff-seed:${session.id}`, seed);
  }

  return `/chat?handoff=${encodeURIComponent(session.id)}&agent=${encodeURIComponent(params.agent.id)}`;
}
